import { useEffect, useRef } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { useGlobalSearch } from '@/hooks/useGlobalSearch'
import { Command } from '@/components/ui/command'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { PaletteInput, SearchPaletteBody } from '@/components/search/SearchPalette'
import { useSearchPalette } from '@/components/search/useSearchPalette'
import { SearchDocumentQuickLook } from '@/components/search/SearchDocumentQuickLook'
import { SearchEmailComposer } from '@/components/search/SearchEmailComposer'
import { cn } from '@/lib/utils'

interface SearchGlobalProps {
    className?: string
    enableAssistant: boolean
    userId: string
}

/** Détection plateforme pour l'indice clavier (⌘K macOS / Ctrl K autres). */
function isMac(): boolean {
    if (typeof navigator === 'undefined') return false
    return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent)
}

/**
 * Recherche globale — DESKTOP (fusion du champ, complément §1).
 *
 * Il n'existe qu'UN champ : ce **vrai champ** (input cmdk) vit dans le header et
 * ne bouge jamais. Au focus / ⌘K, un **panneau ancré** se déploie juste dessous
 * (Radix Popover `usePortal={false}` → reste dans le sous-arbre `Command` pour
 * que la navigation clavier cmdk fonctionne), avec un overlay sombre derrière.
 * Plus de modale centrée ni de second champ. Sur mobile, c'est `GlobalSearchDialog`
 * (plein écran) qui prend le relais — ici on est inerte (`active = !isMobile`).
 */
export function SearchGlobal({ className, enableAssistant, userId }: SearchGlobalProps) {
    const { isOpen, open, close } = useGlobalSearch()
    const isMobile = useIsMobile() ?? false
    const palette = useSearchPalette({ enableAssistant, userId, active: !isMobile })
    const fieldRef = useRef<HTMLDivElement>(null)

    // ⌘K (ou ouverture programmatique) → focus le vrai champ du header.
    useEffect(() => {
        if (isOpen && !isMobile) palette.inputRef.current?.focus()
    }, [isOpen, isMobile, palette.inputRef])

    return (
        <Command
            shouldFilter={false}
            value={palette.selectedValue}
            onValueChange={palette.setSelectedValue}
            onKeyDown={palette.handleCommandKeyDown}
            className="contents"
        >
            <Popover
                open={isOpen && !isMobile}
                onOpenChange={(o) => {
                    if (!o) {
                        // Le lecteur « Aperçu » plein écran (Dialog) s'ouvre PAR-DESSUS la
                        // palette et lui vole le focus → le Popover demande sa fermeture.
                        // Tant que le lecteur est ouvert, on IGNORE cette fermeture : sinon
                        // `close()` déclenche le reset (`quickLook=null`) et le lecteur se
                        // démonte aussitôt (« tout se ferme »). À la fermeture du lecteur,
                        // on revient dans la palette restée ouverte dessous (§6.2).
                        if (palette.quickLook) return
                        palette.rememberQuery()
                        close()
                    }
                }}
            >
                {/* Le champ = ancre du panneau. Au-dessus du scrim quand ouvert. */}
                <PopoverAnchor asChild>
                    <div
                        ref={fieldRef}
                        // Clic/curseur dans le champ → ouvre la drop-down (≠ ⌘K seul).
                        // `onFocus` couvre le 1ᵉʳ focus ; `onPointerDown` couvre le re-clic
                        // alors que le champ a déjà le focus.
                        onPointerDown={open}
                        className={cn(
                            'flex h-9 items-center gap-2 rounded-xl px-3',
                            // Ouvert : au-dessus du scrim (z-50) + fond plein (le champ
                            // reste pleinement éclairé alors que la page s'assombrit).
                            isOpen && 'relative z-50 bg-card',
                            className,
                        )}
                    >
                        <PaletteInput
                            palette={palette}
                            inputRef={palette.inputRef}
                            onFocus={open}
                            inputClassName="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        />
                        {!isOpen && !palette.input && (
                            <kbd className="pointer-events-none hidden shrink-0 select-none rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground md:inline-block">
                                {isMac() ? '⌘K' : 'Ctrl K'}
                            </kbd>
                        )}
                    </div>
                </PopoverAnchor>

                {/* Overlay sombre derrière le panneau (le champ reste éclairé, z-50). */}
                {isOpen && !isMobile && (
                    <div
                        className="fixed inset-0 z-40 bg-foreground/20 motion-safe:animate-in motion-safe:fade-in motion-reduce:bg-foreground/20"
                        aria-hidden="true"
                    />
                )}

                {/* Panneau ancré sous le champ — jaillit du champ (slide-from-top + zoom). */}
                <PopoverContent
                    usePortal={false}
                    align="center"
                    sideOffset={2}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    // Clic DANS le champ (ancre) → ne PAS fermer (sinon l'ouverture au
                    // clic se referait aussitôt). La fermeture reste : Échap, clic dehors.
                    onInteractOutside={(e) => {
                        if (fieldRef.current?.contains(e.target as Node)) e.preventDefault()
                    }}
                    className="z-50 flex max-h-[min(70vh,560px)] w-[min(92vw,820px)] origin-top flex-col overflow-hidden rounded-xl border-border p-0 shadow-2xl"
                >
                    <SearchPaletteBody palette={palette} variant="desktop" />
                </PopoverContent>
            </Popover>

            {palette.quickLook && (
                <SearchDocumentQuickLook
                    open
                    onOpenChange={(o) => {
                        if (!o) palette.setQuickLook(null)
                    }}
                    fileId={palette.quickLook.fileId}
                    filename={palette.quickLook.filename}
                    mime={palette.quickLook.mime}
                    entityId={palette.quickLook.entityId ?? palette.entityId ?? ''}
                />
            )}

            <SearchEmailComposer
                open={!!palette.composeEmail}
                recipientEmail={palette.composeEmail?.email ?? ''}
                entityId={palette.entityId ?? ''}
                onOpenChange={(o) => {
                    if (!o) palette.setComposeEmail(null)
                }}
            />
        </Command>
    )
}

export default SearchGlobal
